import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Play, Pause, Volume2, VolumeX, ArrowRight, CheckCircle, Star, Sparkles,
  Video, Image, Film, Music, Users, Mic, Type, Target, Upload, Hash,
  MousePointer, Layout, FileText, CheckSquare, Globe, Zap, TrendingUp,
  Search, MapPin, Link2, PenTool, Share2, ChevronDown, ChevronRight,
  Monitor, Radio, Tv, Megaphone, Mail, Award, Shield, BadgeCheck,
  Play as PlayIcon, Camera, Clapperboard, Wand2, Palette, MessageSquare, Loader2,
  Rocket, ExternalLink, Scissors, Download, Captions, Volume1
} from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../lib/auth';
import { toast } from 'sonner';

// Hero video URL
const HERO_VIDEO = 'https://storage.googleapis.com/msgsndr/hp00uSQfTXI2CWHYtxP7/media/693b63f7eac0a86fb418b609.mp4';

// Cinematic Ad Examples - with aspect ratio info (portrait = 9:16, landscape = 16:9)
const CINEMATIC_EXAMPLES = [
  { id: 1, url: 'https://storage.googleapis.com/msgsndr/niCJVilKzKOFQ9HmHKz2/media/691b9700c761d44e4dd0a138.mp4', title: 'Brand Story', aspect: 'portrait' },
  { id: 2, url: 'https://storage.googleapis.com/msgsndr/X0MpuAl5pHCfjiyQLY3s/media/693065cdf4c06daee8907ffe.mp4', title: 'Product Launch', aspect: 'portrait' },
  { id: 3, url: 'https://storage.googleapis.com/msgsndr/mEfmjqNqFgIVbmP2rpFb/media/6939e9c4f6cae9cf9feccc8b.mp4', title: 'Service Promo', aspect: 'portrait' },
  { id: 4, url: 'https://storage.googleapis.com/msgsndr/Z36aRQ29AbVmvdff6COg/media/68b0a30a588b86c00f9995ce.mp4', title: 'Local Business', aspect: 'landscape' },
  { id: 5, url: 'https://storage.googleapis.com/msgsndr/mEfmjqNqFgIVbmP2rpFb/media/691e18925e13ec37f808c631.mp4', title: 'Testimonial', aspect: 'portrait' },
];

// Creative Types
const CREATIVE_TYPES = [
  { icon: Video, name: 'Video Ads', desc: 'Full cinematic commercials' },
  { icon: Film, name: 'Reels & Shorts', desc: 'Vertical short-form content' },
  { icon: Image, name: 'Image Ads', desc: 'Static and animated graphics' },
  { icon: Layout, name: 'Carousel Ads', desc: 'Multi-slide experiences' },
  { icon: Sparkles, name: 'GIFs', desc: 'Attention-grabbing animations' },
  { icon: Clapperboard, name: 'Short Films', desc: 'Mini documentaries' },
  { icon: Wand2, name: 'AI Motion Ads', desc: 'AI-generated motion graphics' },
  { icon: Music, name: 'Music & Jingles', desc: 'Custom audio branding' },
  { icon: Mic, name: 'AI Voice Cloning', desc: 'Branded voice synthesis' },
  { icon: Users, name: 'AI Presenters', desc: 'Virtual spokespersons' },
  { icon: Type, name: 'Captioning', desc: 'Multi-language subtitles' },
];

// Ad Tools
const AD_TOOLS = [
  { icon: Type, name: 'Headlines', desc: 'AI-powered headline generator' },
  { icon: PenTool, name: 'Ad Copy', desc: 'Compelling sales copy' },
  { icon: MousePointer, name: 'CTA Creator', desc: 'Call-to-action buttons' },
  { icon: Target, name: 'Audience Builder', desc: 'Custom audience creation' },
  { icon: Upload, name: 'Warm List Upload', desc: 'Import your contacts' },
  { icon: Search, name: 'Keywords', desc: 'Keyword research & targeting' },
  { icon: Hash, name: 'Interests & Tags', desc: 'Interest-based targeting' },
  { icon: Zap, name: 'Pixel Setup', desc: 'Tracking pixel installation' },
  { icon: TrendingUp, name: 'Retargeting', desc: 'Re-engage past visitors' },
  { icon: Layout, name: 'Landing Pages', desc: 'Conversion-optimized templates' },
  { icon: FileText, name: 'Form Templates', desc: 'Lead capture forms' },
  { icon: CheckSquare, name: 'Checklists', desc: 'Campaign launch guides' },
];

// Publishing Channels
const PUBLISHING_CHANNELS = [
  { icon: Globe, name: 'Best of Best Directory', primary: true, desc: 'Your cinematic listing hub' },
  { icon: Share2, name: 'Meta (FB/IG)', desc: 'Facebook & Instagram Ads' },
  { icon: Search, name: 'Google Ads', desc: 'Search & Display Network' },
  { icon: Video, name: 'TikTok', desc: 'Short-form video platform' },
  { icon: PenTool, name: 'Blogs & SEO', desc: 'Content marketing' },
  { icon: Mail, name: 'Email Campaigns', desc: 'Direct outreach' },
  { icon: Tv, name: 'TV & CTV', desc: 'Connected TV advertising' },
  { icon: Radio, name: 'Radio', desc: 'Audio advertising' },
  { icon: Monitor, name: 'Digital Billboards', desc: 'DOOH advertising' },
];

// Fulfillment Services Data
const FULFILLMENT_SERVICES = [
  {
    id: 'meta-ads',
    name: 'Meta Ads',
    tagline: 'Reach more customers online',
    icon: Share2,
    color: 'from-blue-500 to-blue-600',
    description: 'Facebook & Instagram advertising managed by experts',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Monthly Ad Budget: $0 - $2,500',
          '3 Creatives / Month',
          'Pixel & Conversion Setup',
          '1 Audience',
          'Lookalike Audience',
          'Retargeting',
          'Custom Audience',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Monthly Ad Budget: $2,500 - $5,000',
          '6 Creatives / Month',
          'Pixel & Conversion Setup',
          '3 Audiences',
          'Lookalike Audience',
          'Retargeting',
          'Custom Audience',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Monthly Ad Budget: $5,000+',
          '9 Creatives / Month',
          'Pixel & Conversion Setup',
          '5 Audiences',
          'Lookalike Audience',
          'Retargeting',
          'Custom Audience',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'What platforms are included?', a: 'Facebook and Instagram advertising through Meta Business Suite.' },
      { q: 'How are creatives developed?', a: 'Our team creates custom images and videos tailored to your brand.' },
      { q: 'What is a Lookalike Audience?', a: 'An audience of new potential customers who share characteristics with your existing customers.' },
    ]
  },
  {
    id: 'google-ads',
    name: 'Google Ads',
    tagline: 'Show up for high-intent searches',
    icon: Search,
    color: 'from-red-500 to-orange-500',
    description: 'Capture customers actively searching for your services',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Monthly Ad Budget: $0 - $2,500',
          'Search Campaigns',
          'Display Campaigns',
          'Keyword Research',
          'Ad Extensions',
          'Conversion Tracking',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Monthly Ad Budget: $2,500 - $5,000',
          'Search Campaigns',
          'Display Campaigns',
          'Performance Max',
          'Advanced Keyword Research',
          'Ad Extensions',
          'Conversion Tracking',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Monthly Ad Budget: $5,000+',
          'All Campaign Types',
          'YouTube Ads',
          'Performance Max',
          'Advanced Keyword Research',
          'All Ad Extensions',
          'Full Conversion Tracking',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'What campaign types are available?', a: 'Search, Display, Performance Max, and YouTube depending on your plan.' },
      { q: 'How is keyword research conducted?', a: 'We use industry tools to find high-intent, relevant keywords for your business.' },
    ]
  },
  {
    id: 'tiktok-ads',
    name: 'TikTok Ads',
    tagline: 'Get traffic with short-form videos',
    icon: Video,
    color: 'from-pink-500 to-purple-500',
    description: 'Reach younger demographics with engaging video content',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Monthly Ad Budget: $0 - $2,500',
          '3 Ad Creatives / Month',
          'Interest Targeting',
          'Behavior Targeting',
          'Pixel Setup',
          'A/B Ad Testing',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Monthly Ad Budget: $2,500 - $5,000',
          '6 Ad Creatives / Month',
          'Interest Targeting',
          'Behavior Targeting',
          'Custom Audience',
          'Pixel Setup',
          'A/B Ad Testing',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Monthly Ad Budget: $5,000+',
          '9 Ad Creatives / Month',
          'All Targeting Options',
          'Custom Audience',
          'Lookalike Audience',
          'Full Pixel Setup',
          'Advanced A/B Testing',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'What makes TikTok ads effective?', a: 'TikTok reaches engaged audiences with authentic, short-form video content.' },
      { q: 'Who should use TikTok ads?', a: 'Businesses targeting Gen Z and Millennial demographics see the best results.' },
    ]
  },
  {
    id: 'seo',
    name: 'SEO',
    tagline: 'Boost organic traffic to your site',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-500',
    description: 'Long-term organic visibility and traffic growth',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          '10 Keywords Targeted',
          'On-Page Audit',
          'Technical SEO',
          'Content Recommendations',
          'Monthly Reporting',
          'Ranking Tracking',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          '20 Keywords Targeted',
          'Full On-Page Optimization',
          'Technical SEO',
          'Content Creation (2 articles)',
          'Link Building',
          'Bi-Weekly Reporting',
          'Ranking Tracking',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          '40 Keywords Targeted',
          'Full On-Page Optimization',
          'Advanced Technical SEO',
          'Content Creation (4 articles)',
          'Premium Link Building',
          'Local SEO',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'How long until I see results?', a: 'SEO is a long-term strategy. Most clients see meaningful results in 3-6 months.' },
      { q: 'What is technical SEO?', a: 'Optimizing your website structure, speed, and crawlability for search engines.' },
    ]
  },
  {
    id: 'gbp-ranker',
    name: 'GBP Ranker',
    tagline: 'Improve your local search visibility',
    icon: MapPin,
    color: 'from-yellow-500 to-amber-500',
    description: 'Dominate Google Maps and local search results',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'AI Image Optimization: 1/Week',
          'AI Q&A: 1/Week',
          'AI Content Training',
          'AI Custom Tuning',
          'AI Review Replies',
          'Human Content Verification',
          'Weekly Work Logs',
          'Monthly Performance Reports',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'AI Image Optimization: 3/Week',
          'AI Q&A: 3/Week',
          'AI Content Training',
          'AI Custom Tuning',
          'AI Review Replies',
          'Optimized Image Drip',
          'Image Upload Reminders',
          'Human Content Verification',
          'Weekly Work Logs',
          'Monthly Performance Reports',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'AI Image Optimization: Daily',
          'AI Q&A: Daily',
          'AI Content Training',
          'AI Custom Tuning',
          'AI Review Replies',
          'Optimized Image Drip',
          'Image Upload Reminders',
          'Human Content Verification',
          'Custom Requests',
          'Weekly Work Logs',
          'Monthly Performance Reports',
        ]
      }
    ],
    faqs: [
      { q: 'What is AI Review Replies?', a: 'Our AI automatically responds to your Google reviews in your brand voice.' },
      { q: 'How does image optimization help?', a: 'Optimized images with keywords and geo-tags improve local search visibility.' },
    ]
  },
  {
    id: 'backlinks',
    name: 'Backlinks',
    tagline: 'Increase your site authority',
    icon: Link2,
    color: 'from-purple-500 to-violet-500',
    description: 'Build domain authority with quality backlinks',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Domain Authority: DA 10+',
          '10 Backlinks / Month',
          '5 In-Content Links',
          'Social Bookmarks: 5',
          'Q&A Submissions: 2',
          'Image Sharing: 3',
          'Business Profile Links: 2',
          'Directory & Citation Submissions',
          'Monthly Backlink Report',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Domain Authority: DA 30+',
          '15 Backlinks / Month',
          '10 In-Content Links',
          'Social Bookmarks: 10',
          'Q&A Submissions: 3',
          'Image Sharing: 5',
          'Business Profile Links: 3',
          'Web 2.0 Blog Content (500 words)',
          'Web 2.0 Blog Posting',
          'Monthly Backlink Report',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Domain Authority: DA 50+',
          '20 Backlinks / Month',
          '15 In-Content Links',
          'All Basic Link Building',
          'Web 2.0 Blog (1,000 words)',
          'Guest Blog Writing (1,000 words)',
          'Guest Blog Posting',
          'Press Release (1,000 words)',
          'Press Release Submission',
          'Monthly Backlink Report',
        ]
      }
    ],
    faqs: [
      { q: 'What is Domain Authority?', a: 'A score (1-100) predicting how well a site will rank. Higher DA sites provide more valuable backlinks.' },
      { q: 'Are these links white-hat?', a: 'Yes, all links are from legitimate sources following search engine guidelines.' },
    ]
  },
  {
    id: 'content',
    name: 'Content',
    tagline: 'Fuel SEO with optimized content',
    icon: PenTool,
    color: 'from-cyan-500 to-blue-500',
    description: 'AI-powered content creation with human refinement',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          '1 Blog Article / Month',
          '500 Words Per Article',
          'SEO Keyword Boost',
          'AI Enhanced Topic Research',
          'AI GPT-4o Written',
          'Curated Stock Images',
          'Human Reviewed & Refined',
          'Article Publishing',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          '2 Blog Articles / Month',
          '1,000 Words Per Article',
          'SEO Keyword Boost',
          'AI Enhanced Topic Research',
          'AI GPT-4o Written',
          'Curated Stock Images',
          'Human Reviewed & Refined',
          'Article Publishing',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          '4 Blog Articles / Month',
          '2,000 Words Per Article',
          'SEO Keyword Boost',
          'AI Enhanced Topic Research',
          'AI GPT-4o Written',
          'Curated Stock Images',
          'Human Reviewed & Refined',
          'Article Publishing',
        ]
      }
    ],
    faqs: [
      { q: 'Is the content unique?', a: 'Yes, all content is original, AI-generated, and human-reviewed for quality.' },
      { q: 'Can I request specific topics?', a: 'Absolutely! We work with you to create content relevant to your business.' },
    ]
  },
  {
    id: 'social-posts',
    name: 'Social Posts',
    tagline: 'Stay consistent on social media',
    icon: MessageSquare,
    color: 'from-indigo-500 to-purple-500',
    description: 'Professional social media management',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Posting: 1x Per Week',
          '4 Original Posts / Month',
          '1 Social Channel',
          'Licensed Images',
          'Image Submissions',
          'Trending Hashtags',
          'Scheduled Posts',
          '3-Point Inspection',
          'Quality Assurance Audit',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Posting: 4x Per Week',
          '16 Original Posts / Month',
          '3 Social Channels',
          'Posting Time Optimization',
          'Licensed Images',
          'Image Submissions',
          'Trending Hashtags',
          'Scheduled Posts',
          '3-Point Inspection',
          'Quality Assurance Audit',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Posting: Every Day',
          '28 Original Posts / Month',
          '6 Social Channels',
          'Posting Time Optimization',
          'Licensed Images',
          'Image Submissions',
          'Trending Hashtags',
          'Scheduled Posts',
          '3-Point Inspection',
          'Quality Assurance Audit',
          'Monthly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'Which platforms are supported?', a: 'Facebook, Instagram, LinkedIn, Twitter, TikTok, and more based on your selection.' },
      { q: 'What is Posting Time Optimization?', a: 'We schedule posts at peak engagement times using analytics data.' },
    ]
  },
];

// Video Player Component
const VideoPlayer = ({ src, className, autoPlay = true, loop = true, muted = true, controls = false }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className={cn("relative group", className)}>
      <video
        ref={videoRef}
        src={src}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        className="w-full h-full object-cover"
      />
      {controls && (
        <div className="absolute bottom-4 left-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={togglePlay}
            className="p-2 bg-black/60 backdrop-blur-sm rounded-full hover:bg-black/80 transition-colors"
          >
            {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
          </button>
          <button
            onClick={toggleMute}
            className="p-2 bg-black/60 backdrop-blur-sm rounded-full hover:bg-black/80 transition-colors"
          >
            {isMuted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
          </button>
        </div>
      )}
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, onSelect }) => {
  const Icon = service.icon;
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className="group cursor-pointer"
      onClick={() => onSelect(service)}
    >
      <Card className="glass border-white/10 hover:border-primary/50 transition-all h-full overflow-hidden">
        <div className={cn("h-2 w-full bg-gradient-to-r", service.color)} />
        <CardContent className="p-6">
          <div className={cn("w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4", service.color)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-heading font-bold text-white mb-1">{service.name}</h3>
          <p className="text-primary text-sm mb-3">{service.tagline}</p>
          <p className="text-gray-400 text-sm">{service.description}</p>
          <div className="mt-4 flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
            View Plans <ChevronRight className="w-4 h-4 ml-1" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Service Detail Modal
const ServiceDetailModal = ({ service, onClose, onOrder, isOrdering }) => {
  const [activeTab, setActiveTab] = useState('plans');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [orderingPlan, setOrderingPlan] = useState(null);
  const Icon = service.icon;

  const handleOrder = (plan) => {
    setOrderingPlan(plan.name);
    onOrder(service, plan);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={!isOrdering ? onClose : undefined}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-card rounded-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn("p-6 bg-gradient-to-r", service.color)}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-white">{service.name}</h2>
              <p className="text-white/80">{service.tagline}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="plans">Plans & Pricing</TabsTrigger>
              <TabsTrigger value="features">All Features</TabsTrigger>
              <TabsTrigger value="faqs">FAQs</TabsTrigger>
            </TabsList>

            <TabsContent value="plans">
              <div className="grid md:grid-cols-3 gap-6">
                {service.plans.map((plan, idx) => (
                  <Card key={plan.name} className={cn(
                    "relative overflow-hidden",
                    plan.highlight ? "border-primary ring-2 ring-primary/20" : "border-white/10"
                  )}>
                    {plan.highlight && (
                      <div className="absolute top-0 left-0 right-0 bg-primary text-black text-xs font-bold text-center py-1">
                        MOST POPULAR
                      </div>
                    )}
                    <CardHeader className={plan.highlight ? "pt-8" : ""}>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <p className="text-gray-400 text-sm">Contact for Quote</p>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className={cn("w-full mt-6", plan.highlight ? "btn-primary" : "")}
                        variant={plan.highlight ? "default" : "outline"}
                        onClick={() => handleOrder(plan)}
                        disabled={isOrdering}
                      >
                        {isOrdering && orderingPlan === plan.name ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Order...</>
                        ) : (
                          <>Get Started <ArrowRight className="w-4 h-4 ml-2" /></>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="features">
              <Card className="border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">All Services & Features</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {service.plans[2].features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-primary" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="faqs">
              <div className="space-y-3">
                {service.faqs.map((faq, i) => (
                  <Card key={i} className="border-white/10">
                    <button
                      className="w-full p-4 flex items-center justify-between text-left"
                      onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    >
                      <span className="font-medium text-white">{faq.q}</span>
                      <ChevronDown className={cn(
                        "w-5 h-5 text-gray-400 transition-transform",
                        expandedFaq === i && "rotate-180"
                      )} />
                    </button>
                    {expandedFaq === i && (
                      <div className="px-4 pb-4 text-gray-400">
                        {faq.a}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isOrdering}>Close</Button>
          <Button className="btn-primary" onClick={() => handleOrder(service.plans[1])} disabled={isOrdering}>
            {isOrdering && orderingPlan === service.plans[1].name ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Order...</>
            ) : (
              <>Order Now <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function CinematicAdsPage() {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [activeExampleIdx, setActiveExampleIdx] = useState(0);
  const [isOrdering, setIsOrdering] = useState(false);
  const fulfillmentRef = React.useRef(null);
  const examplesRef = React.useRef(null);

  // Seed cinematic services on mount
  useEffect(() => {
    const seedServices = async () => {
      try {
        await api.post('/services/seed-cinematic');
      } catch (error) {
        // Services may already exist, that's ok
        console.log('Cinematic services check completed');
      }
    };
    seedServices();
  }, []);

  // Scroll to fulfillment section
  const scrollToFulfillment = () => {
    fulfillmentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Scroll to examples section
  const scrollToExamples = () => {
    examplesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Create actual order and navigate to order detail
  const handleOrderService = async (service, plan) => {
    setIsOrdering(true);
    try {
      // Map service ID to cinematic service ID
      const serviceIdMap = {
        'meta-ads': 'cinematic-meta-ads',
        'google-ads': 'cinematic-google-ads',
        'tiktok-ads': 'cinematic-tiktok-ads',
        'seo': 'cinematic-seo',
        'gbp-ranker': 'cinematic-gbp-ranker',
        'backlinks': 'cinematic-backlinks',
        'content': 'cinematic-content',
        'social-posts': 'cinematic-social-posts',
      };

      const cinematicServiceId = serviceIdMap[service.id];
      
      // Create order data
      const orderData = {
        service_id: cinematicServiceId,
        summary: `${service.name} - ${plan.name} Plan\n\nSelected Features:\n${plan.features.map(f => `• ${f}`).join('\n')}`,
        structured_data_json: {
          service_type: 'cinematic_fulfillment',
          service_name: service.name,
          plan_name: plan.name,
          plan_features: plan.features,
          tagline: service.tagline,
          description: service.description,
          pricing_model: 'Contact for Quote / Co-Op Balance',
          requested_from: 'cinematic_ads_page'
        },
        files: []
      };

      const response = await api.createOrder(orderData);
      
      if (response.data && response.data.id) {
        toast.success(`Order created for ${service.name} - ${plan.name} Plan!`);
        setSelectedService(null);
        // Navigate directly to the order detail page
        navigate(`/orders/${response.data.id}`);
      }
    } catch (error) {
      console.error('Order creation failed:', error);
      toast.error('Failed to create order. Please try again.');
    } finally {
      setIsOrdering(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Section - Full Screen Video */}
        <section className="relative h-[80vh] overflow-hidden">
          <VideoPlayer 
            src={HERO_VIDEO}
            className="absolute inset-0 w-full h-full"
            controls
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
          
          {/* Hero Content */}
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-2xl"
              >
                <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                  <Clapperboard className="w-3 h-3 mr-1" /> AI Horse-Powered
                </Badge>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                  Cinematic Ad
                  <span className="block text-primary">System</span>
                </h1>
                <p className="text-xl text-gray-300 mb-6">
                  A complete <strong className="text-white">Cinematic Video Ad</strong> with lead-capturing video, 
                  ad copy, landing page, and automated nurture sequences. Leads flow into your CRM where 
                  AI agents and automations convert them into customers — putting you <strong className="text-primary">everywhere at once</strong>.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button className="btn-primary text-lg px-8 py-4" onClick={scrollToFulfillment}>
                    Start Your Cinematic Journey <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-4" onClick={scrollToExamples}>
                    <Play className="w-5 h-5 mr-2" /> Watch Examples
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Publishing Flow Section */}
        <section className="py-16 px-6 bg-card/50 border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
                <Zap className="w-3 h-3 mr-1" /> How It Works
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                One <span className="text-primary">Cinematic Video Ad</span>. Everywhere.
              </h2>
              <p className="text-gray-400 max-w-3xl mx-auto text-lg">
                Your Cinematic Video Ad is a complete system — lead-capturing video, compelling ad copy, 
                high-converting landing page, and automated nurture sequences. Leads flow into your CRM 
                where AI agents, appointment setting, and automations convert them into customers.
              </p>
            </motion.div>

            {/* Publishing Flow Diagram */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
              {PUBLISHING_CHANNELS.map((channel, idx) => (
                <React.Fragment key={channel.name}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className={cn(
                      "flex flex-col items-center p-4 rounded-xl transition-all",
                      channel.primary 
                        ? "bg-primary/20 border-2 border-primary ring-4 ring-primary/20" 
                        : "glass border border-white/10 hover:border-primary/30"
                    )}
                  >
                    <channel.icon className={cn(
                      "w-8 h-8 mb-2",
                      channel.primary ? "text-primary" : "text-gray-400"
                    )} />
                    <span className={cn(
                      "text-sm font-medium text-center",
                      channel.primary ? "text-primary" : "text-white"
                    )}>
                      {channel.name}
                    </span>
                    {channel.primary && (
                      <Badge className="mt-2 text-xs bg-primary text-black">START HERE</Badge>
                    )}
                  </motion.div>
                  {idx < PUBLISHING_CHANNELS.length - 1 && (
                    <ChevronRight className="w-6 h-6 text-gray-600 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Best of Best Directory Explanation */}
        <section className="py-16 px-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-purple-500/5" />
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Badge className="mb-4 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  <Award className="w-3 h-3 mr-1" /> Best of Best Directory
                </Badge>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                  Your <span className="text-yellow-400">Cinematic Listing</span> Hub
                </h2>
                <p className="text-gray-300 mb-6 text-lg">
                  Every client gets a premium cinematic listing in our Best of Best Directory. 
                  This is not just a listing—it is your complete marketing hub featuring:
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    'Your cinematic video showcase',
                    'Complete ad creative gallery',
                    'Landing pages & funnels',
                    'Lead capture forms',
                    'Affiliate program integration',
                    'AI Certified & Best of Best badges'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Card className="glass border-white/10 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Shield className="w-6 h-6 text-primary" />
                      <span className="text-lg font-semibold text-white">Local Endorsement Network</span>
                    </div>
                    <p className="text-gray-400 mb-4">
                      Local businesses lock arms and endorse each other. Mortgage companies, realtors, 
                      and home service pros all showcase each other through the directory.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: MapPin, text: 'Boost Google Maps' },
                        { icon: Users, text: 'Cross-Endorsements' },
                        { icon: BadgeCheck, text: 'AI Certified Badges' },
                        { icon: Globe, text: 'Local Best of Best' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-white/5">
                          <item.icon className="w-4 h-4 text-primary" />
                          <span className="text-sm text-gray-300">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Launch Vector Section - AI Film Creation Hub */}
        <section className="py-20 px-6 bg-gradient-to-br from-purple-900/30 via-indigo-900/20 to-cyan-900/20 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500 rounded-full blur-[100px]" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-500 rounded-full blur-[120px]" />
          </div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-gradient-to-r from-purple-500/30 to-cyan-500/30 text-white border-purple-500/50">
                <Rocket className="w-3 h-3 mr-1" /> Launch Vector
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Create Your <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Cinematic Short Film</span>
              </h2>
              <p className="text-gray-300 max-w-3xl mx-auto text-lg">
                Use our AI-powered <strong className="text-white">Hype Jet 8.2</strong> to generate scripts, storyboards, 
                images, and everything you need to create a stunning short film for your business.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* ChatGPT Embed Section */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Card className="glass border-purple-500/30 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white">Hype Jet 8.2</CardTitle>
                        <p className="text-sm text-gray-400">AI Cinematic Script & Image Generator</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-gray-300 mb-4">
                      Hype Jet creates complete cinematic packages including scripts, shot lists, 
                      AI-generated images, and storyboards for your short film.
                    </p>
                    <div className="space-y-3 mb-6">
                      {[
                        'Generate compelling scripts & narratives',
                        'Create AI images for each scene',
                        'Build complete storyboards',
                        'Get shot-by-shot guidance',
                        'Export assets for video editing'
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                          <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                    <a 
                      href="https://chatgpt.com/g/g-684cf38dd84481918f9ea1c7f04f7562-hype-jet-8-2"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white">
                        Launch Hype Jet 8.2 <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Opens in ChatGPT (requires ChatGPT account)
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Workflow Steps */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Card className="glass border-white/10">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <Film className="w-5 h-5 text-cyan-400" />
                      Complete Your Cinematic Film
                    </CardTitle>
                    <p className="text-sm text-gray-400">Follow these steps to bring your vision to life</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Step 1 */}
                    <div className="flex gap-4 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                      <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center shrink-0">
                        <span className="text-purple-400 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Generate Your Assets</h4>
                        <p className="text-sm text-gray-400">
                          Use <strong className="text-purple-400">Hype Jet 8.2</strong> to create your script, 
                          AI images, and storyboard. Download all your assets.
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                      <div className="w-10 h-10 rounded-full bg-cyan-500/30 flex items-center justify-center shrink-0">
                        <span className="text-cyan-400 font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">Turn Images into Video</h4>
                        <p className="text-sm text-gray-400">
                          Head to your <Link to="/diy-tools" className="text-cyan-400 hover:underline">DIY Tools</Link> page 
                          to transform your AI images into stunning video clips using our video generator.
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex gap-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                      <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center shrink-0">
                        <span className="text-green-400 font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1 flex items-center gap-2">
                          Edit & Enhance <Scissors className="w-4 h-4" />
                        </h4>
                        <p className="text-sm text-gray-400">
                          Use video editing software (CapCut, Premiere, etc.) to connect clips, 
                          add transitions, soundtracks, and effects.
                        </p>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="flex gap-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/30 flex items-center justify-center shrink-0">
                        <span className="text-yellow-400 font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1 flex items-center gap-2">
                          Add Captions & Audio <Captions className="w-4 h-4" />
                        </h4>
                        <p className="text-sm text-gray-400">
                          Add voice-overs, background music, sound effects, and auto-generated captions 
                          for maximum engagement.
                        </p>
                      </div>
                    </div>

                    {/* Step 5 */}
                    <div className="flex gap-4 p-4 rounded-xl bg-primary/10 border border-primary/20">
                      <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center shrink-0">
                        <span className="text-primary font-bold">5</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1 flex items-center gap-2">
                          Download & Publish <Download className="w-4 h-4" />
                        </h4>
                        <p className="text-sm text-gray-400">
                          Export your finished video and publish it across all your marketing channels!
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Alternative CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <Card className="glass border-primary/30 max-w-2xl mx-auto">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Prefer to Have Us Create Your Film?
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Start your <strong className="text-primary">Launch Vector</strong> and let our team handle everything — 
                    from script to final cut.
                  </p>
                  <Button className="btn-primary" onClick={scrollToFulfillment}>
                    Start Your Launch Vector <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Creative Types Section */}
        <section className="py-16 px-6 bg-black/40">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-purple-500/20 text-purple-400 border-purple-500/30">
                <Camera className="w-3 h-3 mr-1" /> Creative Studio
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Cinematic <span className="text-purple-400">Creative Types</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                From full-length commercials to AI-powered motion graphics, we create every type of ad creative you need.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {CREATIVE_TYPES.map((type, idx) => (
                <motion.div
                  key={type.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass rounded-xl p-4 border border-white/10 hover:border-purple-500/50 transition-all text-center group"
                >
                  <type.icon className="w-8 h-8 text-purple-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <h3 className="text-sm font-medium text-white mb-1">{type.name}</h3>
                  <p className="text-xs text-gray-500">{type.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Cinematic Examples Gallery */}
        <section ref={examplesRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                <Film className="w-3 h-3 mr-1" /> Gallery
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Cinematic <span className="text-cyan-400">Video Ad Examples</span>
              </h2>
            </motion.div>

            {/* Video Gallery - Compact layout with thumbnails below */}
            <div className="flex flex-col items-center gap-6">
              {/* Main Video - adapts to aspect ratio */}
              <div className={cn(
                "mx-auto",
                CINEMATIC_EXAMPLES[activeExampleIdx].aspect === 'portrait' 
                  ? "w-[240px] sm:w-[280px] h-[420px] sm:h-[500px]" 
                  : "w-full max-w-xl h-[280px] sm:h-[320px]"
              )}>
                <Card className="overflow-hidden border-white/10 h-full w-full">
                  <VideoPlayer 
                    src={CINEMATIC_EXAMPLES[activeExampleIdx].url}
                    className="h-full w-full"
                    controls
                  />
                </Card>
              </div>
              
              {/* Format label */}
              <p className="text-center text-sm text-gray-400">
                {CINEMATIC_EXAMPLES[activeExampleIdx].aspect === 'portrait' ? 'Reel / Story Format' : 'Landscape / Feed Format'}
              </p>
              
              {/* Thumbnails - horizontal row below video */}
              <div className="flex flex-wrap justify-center gap-3 w-full max-w-3xl">
                {CINEMATIC_EXAMPLES.map((example, idx) => (
                  <button
                    key={example.id}
                    onClick={() => setActiveExampleIdx(idx)}
                    className={cn(
                      "p-3 rounded-lg flex items-center gap-3 transition-all min-w-[180px]",
                      activeExampleIdx === idx 
                        ? "bg-primary/20 border border-primary" 
                        : "glass border border-white/10 hover:border-primary/50"
                    )}
                  >
                    <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center shrink-0">
                      <PlayIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-white text-sm">{example.title}</p>
                      <p className="text-xs text-gray-400">
                        {example.aspect === 'portrait' ? 'Reel' : 'Landscape'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Ad Tools Section */}
        <section className="py-16 px-6 bg-card/50 border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/30">
                <Wand2 className="w-3 h-3 mr-1" /> DIY Tools
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Ad Creation <span className="text-orange-400">Toolkit</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Access templates and tools to create headlines, ad copy, CTAs, audiences, and everything you need to launch campaigns.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {AD_TOOLS.map((tool, idx) => (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass rounded-xl p-5 border border-white/10 hover:border-orange-500/50 transition-all cursor-pointer group"
                >
                  <tool.icon className="w-8 h-8 text-orange-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-medium text-white mb-1">{tool.name}</h3>
                  <p className="text-sm text-gray-500">{tool.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/diy-tools">
                <Button variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
                  Access Cinematic Ad Toolkit <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Fulfillment Center Section */}
        <section ref={fulfillmentRef} className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                <Megaphone className="w-3 h-3 mr-1" /> Fulfillment Center
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Managed <span className="text-primary">Services</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                Let our experts handle fulfillment while you focus on your clients. 
                Choose the services you need to complete your Cinematic Video Ad marketing stack.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {FULFILLMENT_SERVICES.map((service, idx) => (
                <ServiceCard 
                  key={service.id} 
                  service={service} 
                  onSelect={setSelectedService}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-primary/20 via-purple-500/10 to-cyan-500/10 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Go <span className="text-primary">Cinematic</span>?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Start with your video and listing, then watch your brand cascade across every platform.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button className="btn-primary text-lg px-10 py-5" onClick={() => navigate('/order/cinematic-ads')}>
                  Start Your Cinematic Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button variant="outline" className="text-lg px-10 py-5">
                  Contact for Quote
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Service Detail Modal */}
        <AnimatePresence>
          {selectedService && (
            <ServiceDetailModal
              service={selectedService}
              onClose={() => !isOrdering && setSelectedService(null)}
              onOrder={handleOrderService}
              isOrdering={isOrdering}
            />
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
